import type { Plugin } from 'vue'
import type { Router } from 'vue-router'

import NProgress from 'nprogress'

import { autoAnimatePlugin } from '@formkit/auto-animate/vue'
import { setI18nTranslator } from '@proj-airi/stage-ui/utils/i18n-bridge'
import { MotionPlugin } from '@vueuse/motion'
import { createPinia } from 'pinia'
import { setupLayouts } from 'virtual:generated-layouts'
import { createApp, nextTick } from 'vue'
import { createRouter, createWebHashHistory, createWebHistory } from 'vue-router'
import { routes } from 'vue-router/auto-routes'

import * as Sentry from '@sentry/vue'

import App from './App.vue'

import { i18n } from './modules/i18n'

import '@unocss/reset/tailwind.css'
import './styles/fonts.css'
import './styles/main.css'
import 'uno.css'

// 设置 i18n 桥接，让 Pinia store 能访问翻译函数
setI18nTranslator((key: string, defaultMsg?: string) => {
  return i18n.global.t(key, defaultMsg ?? key)
})

const pinia = createPinia()
const routeRecords = setupLayouts(routes)

function resolveHistoryTargetUrl(url: string | URL | null | undefined): string | null {
  if (url == null)
    return null

  try {
    return new URL(String(url), window.location.href).href
  }
  catch {
    return null
  }
}

function isInjectedHistoryDispatchError(error: unknown): boolean {
  return error instanceof TypeError && error.message === 'Cannot read properties of null (reading \'dispatchEvent\')'
}

function patchInjectedHistoryDispatchNoise() {
  const patch = (method: 'pushState' | 'replaceState') => {
    const original = window.history[method].bind(window.history)

    window.history[method] = ((data: unknown, unused: string, url?: string | URL | null) => {
      const targetUrl = resolveHistoryTargetUrl(url)

      try {
        return original(data, unused, url)
      }
      catch (error) {
        if (isInjectedHistoryDispatchError(error) && (targetUrl === null || window.location.href === targetUrl)) {
          return
        }

        throw error
      }
    }) as History[typeof method]
  }

  patch('pushState')
  patch('replaceState')
}

patchInjectedHistoryDispatchNoise()

let router: Router
if (import.meta.env.VITE_APP_TARGET_HUGGINGFACE_SPACE)
  router = createRouter({ routes: routeRecords, history: createWebHashHistory() })
else
  router = createRouter({ routes: routeRecords, history: createWebHistory() })

router.beforeEach((to, from) => {
  if (to.path !== from.path)
    NProgress.start()
})

router.afterEach(() => {
  NProgress.done()
})

// 路由组件解析失败时自动刷新（防止旧缓存导致白屏）
let _routerReloadAttempted = false
router.onError((error) => {
  console.error('Router error:', error)
  const msg = error instanceof Error ? error.message : String(error)
  // 检测 layout/组件解析失败（通常由旧 SW 缓存或 chunk 失效引起）
  if (
    !_routerReloadAttempted
    && (msg.includes('resolve component') || msg.includes('Failed to fetch dynamically imported module') || msg.includes('Loading chunk'))
  ) {
    _routerReloadAttempted = true
    console.warn('⚠️ 路由组件加载失败，尝试刷新页面恢复...')
    window.location.reload()
  }
})

const thirdPartyNetworkErrorPattern = /^(?:Load failed|Failed to fetch|NetworkError when attempting to fetch resource\.?) \(([^)]+)\)$/

function isThirdPartyNetworkNoise(message: string) {
  const match = thirdPartyNetworkErrorPattern.exec(message)
  if (!match)
    return false

  const host = match[1]?.trim().toLowerCase()
  if (!host)
    return false

  const currentHost = globalThis.location?.host?.toLowerCase()
  return host !== currentHost
}

function hasExternalRequestBreadcrumb(event: Sentry.Event) {
  return event.breadcrumbs?.some((crumb) => {
    if (crumb.category !== 'fetch' && crumb.category !== 'xhr')
      return false
    const url = typeof crumb.data?.url === 'string' ? crumb.data.url : ''
    if (!url)
      return false
    try {
      const parsed = new URL(url, window.location.href)
      return parsed.host !== window.location.host
    }
    catch {
      return false
    }
  }) ?? false
}

function isExternalServiceSemanticNoise(message: string) {
  return message.includes('insufficient_user_quota')
    || message.includes('bad_response_status_code')
    || message.includes('model_not_found')
    || message.includes('unknown provider for model')
    || message.includes('RESOURCE_EXHAUSTED')
    || message.includes('exhausted your capacity')
    || message.includes('quota will reset')
    || message.includes('get_channel_failed')
    || message.includes('无可用渠道')
    || message.includes('可用渠道')
    || message.includes('预扣费额度失败')
    || message.includes('用户剩余额度')
    || message.includes('服务器繁忙')
    || message.includes('没有可用账号')
    || message.includes('没有开通账号')
    || message.includes('convert_request_failed')
    || message.includes('failed to decode base64 data')
    || message.includes('get file data')
}

function isExternalServiceHttpStatusNoise(message: string) {
  return message.includes('HTTP 403')
    || message.includes('HTTP 502')
    || message.includes('HTTP 503')
}

function isExternalServiceResponseNoise(message: string, event?: Sentry.Event) {
  if (isExternalServiceSemanticNoise(message))
    return true

  if (!isExternalServiceHttpStatusNoise(message))
    return false

  return event ? hasExternalRequestBreadcrumb(event) : isThirdPartyNetworkNoise(message)
}

function isPluginUrl(value: string) {
  return value.includes('/plugins/')
    || value.includes('ground.nktp.top/plugins/')
    || value.includes(' line 26 > Function')
    || value.includes('line%2026%20%3E%20Function')
}

function containsPluginUrl(value: unknown): boolean {
  if (typeof value === 'string')
    return isPluginUrl(value)

  if (!value || typeof value !== 'object')
    return false

  return Object.values(value as Record<string, unknown>).some(containsPluginUrl)
}

function isPluginRuntimeStack(stack: string) {
  return stack.includes(' line 26 > Function')
    || stack.includes('line%2026%20%3E%20Function')
    || /eval at .*\/assets\/chat-[^/]+\.js:26:/.test(stack)
}

function isPluginRuntimeNoise(event: Sentry.Event, error?: Error) {
  if (error?.stack && isPluginRuntimeStack(error.stack))
    return true

  const values = event.exception?.values ?? []
  if (values.some(value => value.stacktrace?.frames?.some(frame => containsPluginUrl(frame.filename) || containsPluginUrl(frame.abs_path))))
    return true

  return containsPluginUrl(event.request?.url)
}

function isExternalFetchFailureEvent(event: Sentry.Event) {
  const eventMessage = event.exception?.values?.[0]?.value || event.message || ''
  // 'Type error' 是 Safari 对 fetch 网络失败的报错信息（带空格、小写 e）
  if (!['Load failed', 'Failed to fetch', 'Type error'].includes(eventMessage))
    return false

  return event.breadcrumbs?.some((crumb) => {
    if (crumb.category !== 'fetch' && crumb.category !== 'xhr')
      return false
    const url = typeof crumb.data?.url === 'string' ? crumb.data.url : ''
    if (!url)
      return false
    try {
      const parsed = new URL(url, window.location.href)
      return parsed.host !== window.location.host
    }
    catch {
      return false
    }
  }) ?? false
}

function isLive2DTextureNoise(message: string, stack?: string) {
  if (!message.includes('Texture loading error'))
    return false
  // 没栈时无从判断，沿用旧策略放过（与 Model.vue:isLive2DTextureLoadError 同步）
  if (!stack)
    return true
  return stack.includes('pixi')
    || stack.includes('live2d')
    || stack.includes('Live2D')
    || stack.includes('Live2DCubismCore')
}

// PixiJS 6.5.10 内部 loader 的已知缺陷：XHR 出错时其错误处理读取了 responseText，
// 而此时 responseType 已被设为 'arraybuffer'/'json'，触发 InvalidStateError。
// 这是加载失败时的"次生"报错（我们从不自己读 responseText），真正的加载失败已由
// isLive2DTextureNoise / 模型加载兜底处理，故归类为噪音。（Sentry 7515307437）
function isPixiXhrResponseTypeNoise(message: string) {
  return message.includes('Failed to read the \'responseText\' property from \'XMLHttpRequest\'')
}

// transformers.js（ONNX Runtime）的 wasm 文件下载失败：弱网/部署缺资源时，
// emscripten 抛 "Aborted(both async and sync fetching of the wasm failed)"。
// 语义记忆 embedding 的加载方已有 .catch 重置 + 调用方降级（关键词检索兜底），
// 不影响应用，属网络/部署层噪音。（Sentry 7552398021）
// 注意：仅匹配精确的"下载失败"信号。不要泛匹配所有 Aborted(...) + wasm —— 那会连同
// ONNX 推理期的 OOM / 断言失败 / 运行时 abort 一起吞掉，掩盖真实崩溃。
function isWasmFetchNoise(message: string) {
  return message.includes('both async and sync fetching of the wasm failed')
}

function isMediaPlaybackNoise(message: string, errorName?: string, stack?: string) {
  // 仅匹配明确的 HTMLMediaElement 播放错误：自动播放策略 / 媒体源不支持。
  // 不能仅凭 errorName 判断 —— NotAllowedError 也可能来自 getUserMedia / Notification / 支付 API
  const isPlaybackMessage = message.includes('play() can only be initiated by a user gesture')
    || message.includes('The element has no supported sources')
  if (isPlaybackMessage)
    return true
  // 仅当错误名是这两个 + stack 指向 audio/video/media element 时才算播放噪音
  if ((errorName === 'NotAllowedError' || errorName === 'NotSupportedError') && stack) {
    return stack.includes('HTMLMediaElement')
      || stack.includes('audio-record')
      || stack.includes('audioElement')
      || stack.includes('.play(')
  }
  return false
}

function isBareSpecifierNoise(message: string) {
  // 用户/插件代码裸 import 第三方包，浏览器原生 ESM 无法解析（非本体 bug）
  return message.includes('Failed to resolve module specifier')
}

function isAbortNoise(message: string, errorName?: string) {
  return errorName === 'AbortError'
    || message.includes('AbortError')
    || message.includes('signal is aborted without reason')
    || message.includes('The operation was aborted')
}

function isRateLimitNoise(message: string) {
  return message.includes('HTTP 429')
    || message.includes('429 response')
    || message.includes('Too Many Requests')
    || message.includes('rate limit')
    || message.includes('rate_limited')
    || message.includes('最多请求')
}

window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  window.location.reload()
})

// splash 进度控制辅助函数
function splashProgress(percent: number) {
  window.__splashProgress?.(percent)
}
function splashText(text: string) {
  window.__splashText?.(text)
}

function runAfterFirstPaint(task: () => void) {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(task, { timeout: 2500 })
    return
  }

  globalThis.setTimeout(task, 1200)
}

// ⭐ App.vue 初始化完成信号
// App.vue 的 onMounted 完成后会调用 resolve，splash 才会隐藏
let _appReadyResolve: (() => void) | null = null
const appReadyPromise = new Promise<void>((resolve) => {
  _appReadyResolve = resolve
})
window.__appReady = () => {
  _appReadyResolve?.()
}

// ⭐ 封装应用启动流程，确保数据迁移在挂载前完成
async function bootstrap() {
  splashProgress(20)
  splashText('正在迁移数据...')

  // 1. 先执行数据迁移
  try {
    const { runDataMigrations, getMigrationHistory } = await import('./utils/data-migration')
    await runDataMigrations()
    const history = getMigrationHistory()
    console.info('📦 数据迁移状态:', history)

    // 开发环境下暴露迁移工具到 window
    if (import.meta.env.DEV) {
      window.getMigrationHistory = getMigrationHistory
      console.info('💡 开发工具：window.getMigrationHistory() - 查看迁移历史')
    }
  }
  catch (error) {
    console.error('❌ 数据迁移失败，但应用将继续运行:', error)
  }

  // 申请持久化存储，防止浏览器自动清除 PWA 用户数据
  if (navigator.storage?.persist) {
    navigator.storage.persist().then((granted) => {
      console.info(granted ? '✅ 存储已持久化' : '⚠️ 存储仍为 best-effort 模式')
    }).catch(() => {})
  }

  splashProgress(40)
  splashText('正在初始化应用...')

  // 2. 创建并挂载应用
  const app = createApp(App)

  // 初始化 Sentry 错误监控
  Sentry.init({
    app,
    dsn: 'https://d42c7301a025528e07e3c8dd2af37f80@o4510989124173824.ingest.us.sentry.io/4511061616951296',

    // Release 和环境信息（添加 undefined 回退）
    release: typeof __APP_VERSION__ !== 'undefined' && __APP_VERSION__ ? `meowchat@${__APP_VERSION__}` : undefined,
    environment: import.meta.env.MODE, // 'production' | 'development'

    integrations: [
      Sentry.browserTracingIntegration({
        router,
      }),
      Sentry.replayIntegration({
        // 遮罩敏感内容
        maskAllText: false,
        maskAllInputs: true,
        blockAllMedia: false,
        // 仅在错误时记录网络请求
        networkDetailAllowUrls: [window.location.origin],
      }),
    ],

    // 生产环境采样 20% 的请求用于性能追踪
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
    // 生产环境采样 10% 的会话用于回放
    replaysSessionSampleRate: 0.1,
    // 错误发生时 100% 采样回放
    replaysOnErrorSampleRate: 1.0,
    // 只在生产环境启用
    enabled: import.meta.env.PROD,

    // 添加全局标签
    initialScope: {
      tags: {
        'app.version': __APP_VERSION__,
        'app.platform': 'web',
      },
    },

    // 过滤噪音错误，节省配额
    beforeSend(event, hint) {
      // render-blocking asset 性能 hint —— 主 CSS gzip 后约 90KB 已在合理范围，
      // 该信号是偶发噪音，与代码 bug 无关；其它 performance_* 类型保留以便观察
      const issueType = (event as { type?: string, issueType?: string }).issueType
        || (event as { type?: string }).type
      if (issueType === 'performance_render_blocking_asset_span') {
        return null
      }

      const error = hint.originalException
      if (error instanceof Error && isPluginRuntimeNoise(event, error))
        return null
      if (isPluginRuntimeNoise(event) || isExternalFetchFailureEvent(event))
        return null
      if (error instanceof Error) {
        const msg = error.message
        // 过滤用户 API Key 无效的错误（不是 bug）
        if (msg.includes('401') || msg.includes('无效的令牌'))
          return null
        // 过滤 Live2D ZipLoader 已知问题
        if (msg === 'Not implemented' && error.stack?.includes('ZipLoader'))
          return null
        // 过滤浏览器扩展注入的错误
        if (error.stack?.includes('chrome-extension://') || error.stack?.includes('moz-extension://'))
          return null
        // 过滤 vue-i18n JIT 编译器在特定浏览器（MiuiBrowser 等）上的误报
        // 所有翻译文本已验证无语法问题，这是运行时编译器的边缘 case
        if (error instanceof SyntaxError && error.stack?.includes('vue-i18n'))
          return null
        // 过滤常见的网络环境与静态资源加载失败（非代码逻辑 bug）
        if (
          isAbortNoise(msg, error.name)
          || isRateLimitNoise(msg)
          || isMediaPlaybackNoise(msg, error.name, error.stack)
          || isBareSpecifierNoise(msg)
          || (msg.includes('Load failed') && error.stack?.includes('fetch'))
          || (msg.includes('Network error') && error.stack?.includes('XMLHttpRequest'))
          || msg.includes('Unable to preload CSS')
          || isLive2DTextureNoise(msg, error.stack)
          || isPixiXhrResponseTypeNoise(msg)
          || isWasmFetchNoise(msg)
          || isThirdPartyNetworkNoise(msg)
          || isExternalServiceResponseNoise(msg, event)
        ) {
          return null
        }
        // 过滤 LLM 模型幻觉产生的工具调用错误（非代码 bug，是模型行为）
        if (msg.includes('tried to call unavailable tool') || msg.includes('No tools are available')) {
          return null
        }
        // 过滤 xsschema/zod 依赖加载失败（构建缓存问题，非运行时逻辑 bug）
        if (msg.includes('xsschema: Missing') || msg.includes('Missing zod')) {
          return null
        }
        // 过滤浏览器兼容性错误（非代码 bug，是用户浏览器版本过旧）
        if (
          msg.includes('is not a function') && (
            msg.includes('toSorted')
            || msg.includes('toReversed')
            || msg.includes('AbortSignal.any')
            || msg.includes('structuredClone')
          )
        ) {
          return null
        }
        // 过滤 SW 缓存失效导致的动态 import 解构失败（已有 reload 机制兜底）
        if (
          msg.includes('Cannot destructure property')
          || msg.includes('Right side of assignment cannot be destructured')
          || msg.includes('Can\'t find variable')
        ) {
          return null
        }
        // 过滤设备能力限制（非代码 bug）
        if (
          msg.includes('WebGL unsupported')
          || msg.includes('Connection to Indexed Database server lost')
        ) {
          return null
        }
        // 过滤路由组件解析失败（已有 router.onError reload 兜底）
        if (msg.includes('Couldn\'t resolve component')) {
          return null
        }
      }

      const eventType = event.exception?.values?.[0]?.type
      const eventMessage = event.exception?.values?.[0]?.value || event.message || ''
      const eventStack = event.exception?.values?.[0]?.stacktrace?.frames?.map(f => `${f.filename ?? ''} ${f.function ?? ''}`).join('\n') || ''

      if (
        isAbortNoise(eventMessage, eventType)
        || isRateLimitNoise(eventMessage)
        || isMediaPlaybackNoise(eventMessage, eventType, eventStack)
        || isBareSpecifierNoise(eventMessage)
        || eventMessage.includes('Unable to preload CSS')
        || isLive2DTextureNoise(eventMessage, eventStack)
        || isPixiXhrResponseTypeNoise(eventMessage)
        || isWasmFetchNoise(eventMessage)
        || isThirdPartyNetworkNoise(eventMessage)
        || isExternalServiceResponseNoise(eventMessage, event)
      ) {
        return null
      }

      // 上游 LLM 服务的具名错误码（非本体 bug）；'upstream error' 太通用，故不在此过滤
      if (eventMessage.includes('do_request_failed') || eventMessage.includes('new_api_error')) {
        return null
      }

      // 也过滤 event 中的 vue-i18n 错误（来自 Vue error handler）
      if (eventMessage.includes('vue-i18n')) {
        return null
      }
      if (event.exception?.values?.[0]?.type === 'SyntaxError' && event.tags?.['vue-i18n']) {
        return null
      }

      // 过滤 SyntaxError: 2（vue-i18n 编译器错误）
      if (event.exception?.values?.[0]?.type === 'SyntaxError' && event.exception?.values?.[0]?.value === '2') {
        const frames = event.exception?.values?.[0]?.stacktrace?.frames || []
        if (frames.some((f: any) => f.filename?.includes('vue-i18n'))) {
          return null
        }
      }

      // E12: 脱敏 contact 相关字段（联系人名称、ID、角色名等可能包含用户隐私）
      function scrubContactFields(obj: any): any {
        if (!obj || typeof obj !== 'object')
          return obj
        if (Array.isArray(obj))
          return obj.map(scrubContactFields)

        const scrubbed: any = {}
        for (const [key, value] of Object.entries(obj)) {
          const lowerKey = key.toLowerCase()
          // 脱敏字段：contactId / characterId / characterName / contact.name / senderName 等
          if (
            lowerKey.includes('contactid')
            || lowerKey.includes('characterid')
            || lowerKey.includes('charactername')
            || lowerKey.includes('sendername')
            || (lowerKey === 'contact' && value && typeof value === 'object')
            || (lowerKey === 'name' && typeof value === 'string')
          ) {
            scrubbed[key] = '[REDACTED]'
          }
          else if (value && typeof value === 'object') {
            scrubbed[key] = scrubContactFields(value)
          }
          else {
            scrubbed[key] = value
          }
        }
        return scrubbed
      }

      if (event.contexts)
        event.contexts = scrubContactFields(event.contexts)
      if (event.extra)
        event.extra = scrubContactFields(event.extra)
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb: any) => ({
          ...breadcrumb,
          data: scrubContactFields(breadcrumb.data),
        }))
      }

      return event
    },
  })

  app
    .use(MotionPlugin)
    // TODO: Fix autoAnimatePlugin type error
    .use(autoAnimatePlugin as unknown as Plugin)
    .use(router)
    .use(pinia)
    .use(i18n)

  // 过滤 Vue 内部 _cache 警告（运行时编译插件模板的已知行为）
  app.config.warnHandler = (msg, _instance, _trace) => {
    if (msg.includes('"_cache"'))
      return
    console.warn(`[Vue warn]: ${msg}`)
  }

  // 全局 Vue 错误处理器：捕获异步组件加载失败等未被组件内 onErrorCaptured 捕获的错误
  app.config.errorHandler = (err, _instance, info) => {
    console.error(`[Vue Error] ${info}:`, err)
    // Sentry 会自动捕获并应用 beforeSend 过滤规则
  }

  let mountEl = document.querySelector('#app')
  for (let i = 0; i < 3 && !mountEl; i++) {
    await new Promise(resolve => setTimeout(resolve, 200))
    mountEl = document.querySelector('#app')
  }

  if (!mountEl) {
    // 若 DOM 中存在已挂载实例，通常是浏览器扩展 / 重复 bootstrap 导致的副本，静默返回
    // 否则是真实的模板问题（index.html 缺 #app 或 DOMContentLoaded 时序异常），必须上报
    const alreadyMounted = document.querySelector('[data-app-mounted="true"]') !== null
    if (!alreadyMounted) {
      const err = new Error('bootstrap: #app mount element missing after retries')
      console.error(err)
      try {
        Sentry.captureException(err)
      }
      catch {
        // Sentry 可能尚未初始化（本函数里已先 init，但以防万一）
      }
    }
    return
  }

  mountEl.setAttribute('data-app-mounted', 'true')
  app.mount(mountEl)

  splashProgress(60)
  splashText('正在加载数据...')

  // 3. 等待 App.vue onMounted 中的异步初始化完成
  // App.vue 会在所有 store 初始化完成后调用 window.__appReady()
  // 设置 8 秒超时，防止 App.vue 初始化卡住
  await Promise.race([
    appReadyPromise,
    new Promise<void>(resolve => setTimeout(resolve, 8000)),
  ])

  splashProgress(90)
  splashText('即将就绪...')

  // 4. 淡出并移除开场加载动画
  await nextTick()

  // 清除兜底定时器
  if (window.__splashFallback) {
    clearTimeout(window.__splashFallback)
  }

  // 使用 index.html 中定义的 __splashHide（会先推到 100% 再淡出）
  window.__splashHide?.()

  // 5. 首屏稳定后再初始化后台系统，避免冷启动时和聊天界面争抢主线程与网络。
  runAfterFirstPaint(() => {
    import('./composables/init-background-systems').then((mod) => {
      mod?.initBackgroundSystems()
    }).catch((err) => {
      console.warn('⚠️ 后台系统初始化模块加载失败:', err)
    })
  })
}

// 🔧 全局错误处理：捕获未处理的 Promise 拒绝
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason
    const errorName = error && typeof error === 'object' && 'name' in error
      ? String((error as { name?: unknown }).name ?? '')
      : ''
    const errorMessage = error instanceof Error ? error.message : String(error)
    const errorStack = error instanceof Error ? error.stack : undefined

    if (
      isAbortNoise(errorMessage, errorName)
      || isRateLimitNoise(errorMessage)
      || isMediaPlaybackNoise(errorMessage, errorName, errorStack)
      || isBareSpecifierNoise(errorMessage)
      || errorMessage.includes('Unable to preload CSS')
      || isLive2DTextureNoise(errorMessage, errorStack)
      || isPixiXhrResponseTypeNoise(errorMessage)
      || isWasmFetchNoise(errorMessage)
      || isThirdPartyNetworkNoise(errorMessage)
      || isExternalServiceResponseNoise(errorMessage)
    ) {
      event.preventDefault()
      return
    }

    // 静默已知的第三方库错误
    if (error instanceof Error) {
      const msg = error.message
      // pixi-live2d-display ZipLoader 未实现 zipReader（Cubism 2 已知问题）
      if (msg === 'Not implemented' && error.stack?.includes('ZipLoader')) {
        event.preventDefault()
        return
      }
      // removeChild 错误来自统计脚本注入（aegis）
      if (msg.includes('removeChild') && error.stack?.includes('main.') && error.stack?.includes('.js:1:')) {
        event.preventDefault()
        return
      }
    }

    console.error('❌ 未处理的 Promise 拒绝:', error)

    // 如果是 XSAIError（API 错误），提供更友好的错误信息
    if (error && typeof error === 'object' && errorName === 'XSAIError') {
      if (errorMessage.includes('401') || errorMessage.includes('无效的令牌')) {
        console.warn('⚠️ API 认证失败：请检查 API Key 是否有效')
        event.preventDefault()
        return
      }
      // 503 上游服务繁忙（已有重试机制，不需要额外处理）
      if (errorMessage.includes('503') || errorMessage.includes('暂时繁忙')) {
        event.preventDefault()
        return
      }
    }

    // 过滤包含 details/message/status 键的 API 错误对象（Sentry 5E）
    if (error && typeof error === 'object' && 'details' in error && 'message' in error && 'status' in error) {
      console.warn('⚠️ API 错误已被捕获:', error)
      event.preventDefault()
    }
  })
}

// 启动应用（确保 DOM 解析完成）
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => bootstrap())
}
else {
  bootstrap()
}

// 禁止移动端浏览器的双击缩放
if (typeof window !== 'undefined' && 'ontouchend' in window.document) {
  let lastTouchEnd = 0
  document.addEventListener('touchend', (event) => {
    const now = Date.now()
    if (now - lastTouchEnd <= 300) {
      event.preventDefault()
    }
    lastTouchEnd = now
  }, false)
}
